import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/admin/shared/PageHeader";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { PennButton, PennField, PennInput, PennDropdown, PennMultiSelect, PennText } from "@/components/ui";
import { useInfiniteAllApplications } from "@/hooks/useApplications";
import { useInfiniteProductsAdmin } from "@/hooks/useProductsAdmin";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useEvaluateCostModel, type EvaluateResult } from "@/hooks/useCalculate";
import type { MachineCostMode, PerAppCostBreakdown, RegionCode } from "@/api/calculate";

const REGION_OPTIONS = [
  { value: "EU", label: "EU" },
  { value: "NA", label: "NA" },
  { value: "ASIA", label: "ASIA" },
];

// const COST_MODE_OPTIONS = [
//   { value: "dedicated", label: "Dedicated (100% utilization)" },
//   { value: "shared", label: "Shared" },
// ];

const CURRENCY_SYMBOL: Record<string, string> = { EUR: "€", USD: "$" };

/** Reference catalog of cost-model variables (informational — names/scopes/units). */
type CostModelVariable = { key: string; scope: string; desc: string; unit: string };

const COST_MODEL_VARIABLES: CostModelVariable[] = [
  { key: "machinePrice", scope: "runtime", desc: "Resolved machine base price (region-dependent)", unit: "currency" },
  { key: "usefulLifeYears", scope: "user", desc: "Depreciation years", unit: "years" },
  { key: "daysPerYear", scope: "user", desc: "Working days per year", unit: "days" },
  { key: "hoursPerDay", scope: "user", desc: "Working hours per day", unit: "hours" },
  { key: "oeeAuto", scope: "user", desc: "OEE / availability for automated time model", unit: "ratio (0..1)" },
  { key: "oeeManual", scope: "user", desc: "OEE / availability for manual time model", unit: "ratio (0..1)" },
  { key: "laborRatePerHour", scope: "user", desc: "Operator hourly rate", unit: "currency/hour" },
  { key: "scrapManual", scope: "user", desc: "Manual defect rate", unit: "ratio (0..1)" },
  { key: "scrapAuto", scope: "user", desc: "Auto defect rate", unit: "ratio (0..1)" },
  { key: "annualVolume", scope: "application", desc: "Parts/year", unit: "pcs/year" },
  { key: "batchSize", scope: "application", desc: "Batch size", unit: "pcs" },
  { key: "totalFastenersPerPart", scope: "application", desc: "Sum of qtyPerPartA/qtyPerPartB (or qtyPerPart) across fasteners", unit: "pcs/part" },
  { key: "typesPerPart", scope: "application", desc: "Distinct fastener types per part", unit: "count" },
  { key: "autoFedFastenersPerPart", scope: "derived", desc: "Max qtyPerPartA/qtyPerPartB (or qtyPerPart) per type", unit: "pcs/part" },
  { key: "manualFastenersPerPart", scope: "derived", desc: "totalFastenersPerPart - autoFedFastenersPerPart", unit: "pcs/part" },
  { key: "autoFedFastenersPerPart2", scope: "derived", desc: "Sum of top-2 qtyPerPartA/qtyPerPartB (or qtyPerPart) types", unit: "pcs/part" },
  { key: "manualFastenersPerPart2", scope: "derived", desc: "totalFastenersPerPart - autoFedFastenersPerPart2", unit: "pcs/part" },
  { key: "autoFedFastenersPerPart4", scope: "derived", desc: "Sum of top-4 qtyPerPartA/qtyPerPartB (or qtyPerPart) types", unit: "pcs/part" },
  { key: "manualFastenersPerPart4", scope: "derived", desc: "totalFastenersPerPart - autoFedFastenersPerPart4", unit: "pcs/part" },
  { key: "secondsPerHour", scope: "global", desc: "3600", unit: "sec" },
  { key: "qualityFixCost", scope: "global", desc: "Legacy rework cost per defect (deprecated)", unit: "currency/defect" },
  { key: "reworkCost", scope: "user", desc: "Rework cost per defect", unit: "currency/defect" },
  { key: "pickPartSec", scope: "model.constants", desc: "Pick part time", unit: "sec" },
  { key: "placePartSec", scope: "model.constants", desc: "Place part time", unit: "sec" },
  { key: "autoInsertSec", scope: "model.constants", desc: "Insert time with auto feed (per fastener)", unit: "sec" },
  { key: "manualInsertSec", scope: "model.constants", desc: "Insert time manual (per fastener)", unit: "sec" },
  { key: "setupAutoToolingSec", scope: "model.constants", desc: "Setup time for auto tooling", unit: "sec" },
  { key: "setupManualToolingSec", scope: "model.constants", desc: "Setup time per manual tooling", unit: "sec" },
  { key: "forceAdjustSec", scope: "model.constants", desc: "Manual force adjustment time (per type)", unit: "sec" },
  { key: "loadPartSec", scope: "model.constants", desc: "Manual load time per part", unit: "sec" },
  { key: "unloadPartSec", scope: "model.constants", desc: "Manual unload time per part", unit: "sec" },
  { key: "toolChangeSec", scope: "model.constants", desc: "Tool change time per type switch", unit: "sec" },
  { key: "setupToolingMin", scope: "model.constants", desc: "Tooling setup time per type", unit: "min" },
  { key: "setupFixtureMin", scope: "model.constants", desc: "Fixture setup time per batch", unit: "min" },
];

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/** Format a decimal string to fixed dp without float (§L — never Number(price)). */
const fmtNum = (v: string | null | undefined, dp = 2): string => {
  if (v == null || v === "") return "—";
  const [intPart, frac = ""] = v.split(".");
  return dp === 0 ? intPart : `${intPart}.${`${frac}00`.slice(0, dp)}`;
};

const fmtMoney = (v: string | null | undefined, currency: string): string => {
  if (v == null || v === "") return "—";
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  return `${sym}${fmtNum(v)}`;
};

const KpiCard = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
    <PennText as="p" size="xs" color="blue_grey" className="uppercase">
      {label}
    </PennText>
    <PennText as="p" size="lg" color="blue_grey1" font="semibold" className="mt-1">
      {value}
    </PennText>
  </div>
);

export const CostModelToolsPage = () => {
  const navigate = useNavigate();
  const [showVars, setShowVars] = useState(false);
  const [region, setRegion] = useState<RegionCode>("EU");
  const [productId, setProductId] = useState("");
  // Machine cost mode is fixed to "dedicated" (cost-mode picker disabled below).
  const [machineCostMode] = useState<MachineCostMode>("dedicated");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [applicationIds, setApplicationIds] = useState<string[]>([]);
  const [result, setResult] = useState<EvaluateResult | null>(null);
  const [evalError, setEvalError] = useState<string | null>(null);

  const [productSearch, setProductSearch] = useState("");
  const [appSearch, setAppSearch] = useState("");
  // Remembered labels for chosen items so they stay visible when a search filters
  // them out of the current page (single product + multi application chips).
  const [selectedProduct, setSelectedProduct] = useState<{ value: string; label: string } | null>(null);
  const [selectedAppLabels, setSelectedAppLabels] = useState<Record<string, string>>({});

  const debouncedProductSearch = useDebouncedValue(productSearch);
  const debouncedAppSearch = useDebouncedValue(appSearch);
  const debouncedOwnerEmail = useDebouncedValue(ownerEmail);

  const products = useInfiniteProductsAdmin({ limit: 10, search: debouncedProductSearch || undefined });
  const apps = useInfiniteAllApplications({
    limit: 20,
    search: debouncedAppSearch || undefined,
    region,
    userEmail: debouncedOwnerEmail.trim() || undefined,
  });
  const evaluate = useEvaluateCostModel();

  const productOptions = useMemo(() => {
    const opts = (products.data?.pages.flatMap((p) => p.data) ?? []).map((p) => ({
      value: p.productId,
      label: p.productName?.trim() ? `${p.productId} — ${p.productName}` : p.productId,
    }));
    if (selectedProduct && !opts.some((o) => o.value === selectedProduct.value)) {
      return [selectedProduct, ...opts];
    }
    return opts;
  }, [products.data, selectedProduct]);

  const appOptions = useMemo(() => {
    const opts = (apps.data?.pages.flatMap((p) => p.items) ?? []).map((a) => ({
      value: a.applicationId,
      label: `${a.name?.trim() || a.applicationNumber?.trim() || a.applicationId}${
        a.createdByEmail ? ` — ${a.createdByEmail}` : ""
      }`,
    }));
    const present = new Set(opts.map((o) => o.value));
    const selected = applicationIds
      .filter((id) => !present.has(id))
      .map((id) => ({ value: id, label: selectedAppLabels[id] ?? id }));
    return [...selected, ...opts];
  }, [apps.data, applicationIds, selectedAppLabels]);

  // Region scopes the application list, so a region switch invalidates any
  // applications picked under the previous region — clear the selection.
  const handleRegionChange = (value: string) => {
    setRegion(value as RegionCode);
    setApplicationIds([]);
    setSelectedAppLabels({});
  };

  const handleProductChange = (value: string) => {
    setProductId(value);
    setSelectedProduct(
      productOptions.find((o) => o.value === value) ?? (value ? { value, label: value } : null),
    );
  };

  const handleAppChange = (ids: string[]) => {
    setApplicationIds(ids);
    setSelectedAppLabels((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        if (!next[id]) next[id] = appOptions.find((o) => o.value === id)?.label ?? id;
      }
      return next;
    });
  };

  const varColumns: ColDef<CostModelVariable>[] = [
    { header: "Variable", render: (r) => <span className="font-mono text-small">{r.key}</span> },
    { header: "Scope", render: (r) => <span className="font-mono text-small">{r.scope}</span> },
    { header: "Description", key: "desc" },
    { header: "Unit", render: (r) => r.unit || "—" },
  ];

  const perAppRows: (PerAppCostBreakdown & { _key: string })[] =
    result?.response.ok && result.response.available
      ? result.response.result.perApp.map((p, i) => ({ ...p, _key: p.applicationId ?? `${p.applicationName}-${i}` }))
      : [];

  const currency =
    result?.response.ok && result.response.available ? result.response.result.currencyCode : "EUR";

  const perAppColumns: ColDef<PerAppCostBreakdown & { _key: string }>[] = [
    { header: "Application", render: (r) => r.applicationName ?? "—" },
    { header: "Cost / part", render: (r) => fmtMoney(r.costPerPart, currency) },
    { header: "Machine / part", render: (r) => fmtMoney(r.machineCostPerPart, currency) },
    { header: "Worker / part", render: (r) => fmtMoney(r.workerCostPerPart, currency) },
    { header: "Quality / part", render: (r) => fmtMoney(r.qualityCostPerPart, currency) },
    { header: "Req. machines", render: (r) => fmtNum(r.requiredMachines, 2) },
    { header: "Batches / yr", render: (r) => fmtNum(r.batchesPerYear, 0) },
  ];

  const onEvaluate = () => {
    setEvalError(null);
    setResult(null);
    evaluate.mutate(
      { region, productId, machineCostMode, applicationIds },
      {
        onSuccess: (res) => setResult(res),
        onError: (e) => setEvalError(apiErrorMessage(e, "Evaluation failed.")),
      },
    );
  };

  const canEvaluate = productId.trim().length > 0 && applicationIds.length > 0 && !evaluate.isPending;

  const resp = result?.response;

  return (
    <>
      <PageHeader
        title="Cost Model Tools"
        subtitle="View available variables and evaluate a cost model against real applications — resolved machine price, variable values, per-application outputs and aggregated KPIs."
      />

      {/* Calculation Parameters shortcut (POC "Open parameters") */}
      <section className="mb-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <PennText as="h2" size="sm" color="blue_grey1" font="semibold">
              Calculation Parameters
            </PennText>
            <PennText as="p" size="xs" color="blue_grey" className="mt-1">
              Edit global calculation constants and machine-specific timing/investment parameters used by the cost models.
            </PennText>
          </div>
          <PennButton variant="primary" size="sm" onClick={() => navigate("/admin/datasets/calculation-parameters")}>
            Open parameters
          </PennButton>
        </div>
      </section>

      {/* Variable list */}
      <section className="mb-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <PennText as="h2" size="sm" color="blue_grey1" font="semibold">
              Available variables
            </PennText>
            <PennText as="p" size="xs" color="blue_grey" className="mt-1">
              Global defaults and per-cost-model constants available to formulas.
            </PennText>
          </div>
          <PennButton variant="secondary" size="sm" onClick={() => setShowVars((v) => !v)}>
            {showVars ? "Hide variable list" : "Load variable list"}
          </PennButton>
        </div>

        {showVars && (
          <div className="mt-4 max-h-105 overflow-y-auto rounded-2xl">
            <AdminTable
              columns={varColumns}
              rows={COST_MODEL_VARIABLES}
              keyExtractor={(r) => r.key}
              emptyMessage="No variables found."
            />
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evaluation Context */}
        <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
          <PennText as="h2" size="sm" color="blue_grey1" font="semibold" className="uppercase">
            Evaluation Context
          </PennText>
          <PennText as="p" size="xs" color="blue_grey"  className="mb-4">
          Machine Cost Mode is Dedicated (100% utilization)
          </PennText>

          <div className="space-y-4">
            <PennField label="Region" htmlFor="cmt-region">
              <PennDropdown
                inputId="cmt-region"
                options={REGION_OPTIONS}
                value={region}
                onChange={handleRegionChange}
              />
            </PennField>

            <PennField label="Machine type / Product ID" htmlFor="cmt-product" hint="e.g. 824MSPe">
              <PennDropdown
                inputId="cmt-product"
                options={productOptions}
                value={productId}
                onChange={handleProductChange}
                isSearchable
                isLoading={products.isLoading || products.isFetching}
                onInputChange={setProductSearch}
                onMenuScrollToBottom={() => {
                  if (products.hasNextPage && !products.isFetchingNextPage) {
                    void products.fetchNextPage();
                  }
                }}
                placeholder="Search machine type…"
              />
            </PennField>

            {/* <PennField label="Machine cost mode" htmlFor="cmt-mode">
              <PennDropdown
                inputId="cmt-mode"
                options={COST_MODE_OPTIONS}
                value={machineCostMode}
                onChange={(v) => setMachineCostMode(v as MachineCostMode)}
              />
            </PennField> */}

            <PennField label="User email (optional)" htmlFor="cmt-owner" hint="Filters the application list by owner">
              <PennInput
                id="cmt-owner"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="customer@example.com"
              />
            </PennField>

            <PennField label="Applications IDs (multi-select)" htmlFor="cmt-apps" hint="Select one or more applications to cost">
              <PennMultiSelect
                inputId="cmt-apps"
                options={appOptions}
                value={applicationIds}
                onChange={handleAppChange}
                isLoading={apps.isLoading || apps.isFetching}
                onInputChange={setAppSearch}
                onMenuScrollToBottom={() => {
                  if (apps.hasNextPage && !apps.isFetchingNextPage) {
                    void apps.fetchNextPage();
                  }
                }}
                placeholder="Search applications…"
              />
            </PennField>

            <div className="flex items-center gap-3 pt-1">
              <PennButton variant="primary" size="md" onClick={onEvaluate} disabled={!canEvaluate}>
                {evaluate.isPending ? "Evaluating…" : "Evaluate"}
              </PennButton>
              {!canEvaluate && !evaluate.isPending && (
                <PennText as="span" size="xs" color="blue_grey">
                  Enter a product ID and select at least one application.
                </PennText>
              )}
            </div>

            {evalError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
                {evalError}
              </div>
            )}
          </div>
        </section>

        {/* Result + Trace */}
        <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
          <PennText as="h2" size="sm" color="blue_grey1" font="semibold" className="mb-4 uppercase">
            Result + Trace
          </PennText>

          {!resp ? (
            <PennText as="p" size="sm" color="blue_grey">
              Run an evaluation to see the resolved machine price, per-application outputs and aggregated KPIs.
            </PennText>
          ) : resp.ok && !resp.available ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <PennText as="p" size="sm" font="semibold" color="blue_grey1">
                Not available
              </PennText>
              <PennText as="p" size="xs" color="blue_grey" className="mt-1">
                Reason: <span className="font-mono">{resp.reason}</span> (§D.4 — no cost-model fallback / no synthetic cost).
              </PennText>
            </div>
          ) : resp.ok && resp.available ? (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <KpiCard label="Machine cost / part" value={fmtMoney(resp.result.summary.machineCostPerPart, currency)} />
                <KpiCard label="Cost / part" value={fmtMoney(resp.result.summary.costPerPart, currency)} />
                <KpiCard label="Req. machines" value={fmtNum(resp.result.summary.requiredMachines, 2)} />
                <KpiCard label="Req. operators" value={fmtNum(resp.result.summary.requiredOperators, 2)} />
                <KpiCard label="Machine util." value={fmtNum(resp.result.summary.machineUtilization, 2)} />
                <KpiCard label="Worker util." value={fmtNum(resp.result.summary.workerUtilization, 2)} />
              </div>

              <div>
                <PennText as="h3" size="xs" color="blue_grey" font="semibold" className="mb-2 uppercase">
                  Aggregated KPIs
                </PennText>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <KpiCard label="Investment" value={fmtMoney(resp.result.aggregate.investmentCost, currency)} />
                  <KpiCard label="Operator" value={fmtMoney(resp.result.aggregate.operatorCost, currency)} />
                  <KpiCard label="Quality" value={fmtMoney(resp.result.aggregate.qualityCost, currency)} />
                  <KpiCard label="Total" value={fmtMoney(resp.result.aggregate.totalCost, currency)} />
                </div>
              </div>

              <div>
                <PennText as="h3" size="xs" color="blue_grey" font="semibold" className="mb-2 uppercase">
                  Per-application outputs
                </PennText>
                <AdminTable
                  columns={perAppColumns}
                  rows={perAppRows}
                  keyExtractor={(r) => r._key}
                  emptyMessage="No per-application rows."
                />
              </div>

              {result && result.skippedIds.length > 0 && (
                <PennText as="p" size="xs" color="blue_grey">
                  Skipped {result.skippedIds.length} application(s) missing volume, batch size or fasteners.
                </PennText>
              )}
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
};
